import React, { useState, useEffect, useRef } from 'react';
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import LoadingPanel from './components/LoadingPanel';
import ImageCarousel from './components/ImageCarousel';
import { supabase } from './supabaseClient';
import { usePageContent } from './hooks/usePageContent';

interface Application {
  question1: string;
  question2: string;
  question3: string;
  question4: string;
}

const App: React.FC = () => {
  const { content, loading } = usePageContent();
  const [showApplication, setShowApplication] = useState(false);
  const [formData, setFormData] = useState<Application>({
    question1: '',
    question2: '',
    question3: '',
    question4: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const shiftSectionRef = useRef<HTMLDivElement>(null);
  const investmentTilesRef = useRef<HTMLDivElement>(null);
  const [showCookieConsent, setShowCookieConsent] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const shiftSection = shiftSectionRef.current;
    const investmentTiles = investmentTilesRef.current;

    if (shiftSection) {
      observer.observe(shiftSection);
    }

    if (investmentTiles) {
      observer.observe(investmentTiles);
    }

    return () => {
      if (shiftSection) {
        observer.unobserve(shiftSection);
      }
      if (investmentTiles) {
        observer.unobserve(investmentTiles);
      }
    };
  }, []);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowCookieConsent(true);
    }
  }, []);

  const handleCookieConsent = (accepted: boolean) => {
    localStorage.setItem('cookieConsent', accepted ? 'accepted' : 'declined');
    setShowCookieConsent(false);
  };

  const handleInputChange = (field: keyof Application, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase
        .from('applications')
        .insert([formData]);

      if (error) throw error;

      setSubmitStatus('success');
      setFormData({
        question1: '',
        question2: '',
        question3: '',
        question4: ''
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingPanel />;
  }

  if (showApplication) {
    return (
      <div className="min-h-screen bg-navy">
        <div className="section-padding">
          <div className="container-narrow">
            <button 
              onClick={() => setShowApplication(false)}
              className="mb-8 text-gold hover:text-blue transition-colors"
            >
              ← Back
            </button>
            
            <h1 className="text-4xl font-light text-blue mb-6">
              Apply for a Private Inner Wealth Conversation
            </h1>
            
            <p className="text-lg text-blue mb-12 leading-relaxed">
              Please answer the questions below honestly and thoughtfully. Only applications that demonstrate readiness, emotional maturity, and financial capacity will be invited to a call.
            </p>

            <form onSubmit={handleSubmit} className="space-y-8">
              {content.application?.questions?.map((question: string, index: number) => (
                <div key={index}>
                  <label className="block text-blue font-medium mb-3">
                    {index + 1}. {question}
                  </label>
                  <textarea
                    value={formData[`question${index + 1}` as keyof Application]}
                    onChange={(e) => handleInputChange(`question${index + 1}` as keyof Application, e.target.value)}
                    className="w-full p-4 bg-navy-light border border-gold rounded-lg focus:ring-2 focus:border-transparent resize-none text-blue"
                    rows={index === 2 ? 3 : 4}
                    required
                  />
                </div>
              ))}

              {submitStatus === 'success' && (
                <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800">
                    Thank you for your application. We'll review it carefully and be in touch if it appears to be a good fit.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">
                    There was an error submitting your application. Please try again.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy">
      <NavBar onApplyClick={() => setShowApplication(true)} />
      
      {/* Hero Section */}
      <section id="home" className="hero-section-wrapper">
        <div className="hero-frame">
          <div className="hero-video-section">
            {/* Background Video */}
            <div className="video-background">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="hero-video"
                onLoadedMetadata={(e) => { (e.target as HTMLVideoElement).playbackRate = 0.5; }}
              >
                <source src="/IMG_2241.MOV" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="video-overlay"></div>
            </div>

            {/* Content Over Video */}
            <div className="hero-content">
              <div className="max-w-xl mx-auto md:ml-auto md:mr-8 px-4 text-center md:text-right">
                <h1 className="sm: mt-12 text-3xl md:text-5xl font-light text-white mb-6 leading-tight hero-title">
                  {content.hero?.title?.split('—').map((part: string, i: number) => (
                    <React.Fragment key={i}>
                      {part.trim()}
                      {i < content.hero.title.split('—').length - 1 && <><br />— </>}
                    </React.Fragment>
                  ))}
                </h1>
                <p className="text-lg text-blue mb-8 leading-relaxed hero-subtitle">
                  {content.hero?.subtitle}
                </p>
                <button 
                  onClick={() => setShowApplication(true)}
                  className="btn-primary mx-auto md:ml-auto block"
                >
                  {content.hero?.cta || 'Apply for a Complimentary 30-Minute Call'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Unseen Problem */}
      <section id="problem" className="problem-section-wrapper">
        <div className="problem-frame">
          <div className="problem-video-section">
            {/* Background Video */}
            <div className="video-background">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="hero-video"
              >
                <source src="/Rich people.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="video-overlay"></div>
            </div>

            {/* Content Over Video */}
            <div className="problem-content">
              <div className="max-w-5xl mx-auto px-4 md:px-6">
                <div className="text-center mb-16">
                  <h2 className="text-4xl md:text-5xl font-light text-white mb-6">{content.problem?.title}</h2>
                </div>
                
                <div className="grid md:grid-cols-2 gap-12 mb-12">
                  <div className="space-y-6 text-center md:text-left">
                    {content.problem?.intro?.map((text: string, index: number) => (
                      <p key={index} className="text-xl text-white leading-relaxed">{text}</p>
                    ))}
                  </div>
                  
                  <div className="bg-white p-6 md:p-8 rounded-lg shadow-card">
                    <p className="text-lg mb-6 font-medium" style={{ color: '#5B8DB8' }}>{content.problem?.struggles_title}</p>
                    <ul className="space-y-4" style={{ color: '#5B8DB8' }}>
                      {content.problem?.struggles?.map((struggle: string, index: number) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-3 text-xl" style={{ color: '#7BA3C9' }}>✦</span>
                          <span>{struggle}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="text-center">
                  <p className="text-2xl font-light text-white italic border-t border-b border-white py-6 inline-block px-12">
                    {content.problem?.conclusion}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Stefy */}
      <section id="about" className="section-padding bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="text-4xl md:text-5xl font-light text-navy mb-8 text-center">{content.about?.title}</h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-4 text-lg text-navy leading-relaxed text-center md:text-justify">
              {content.about?.paragraphs?.map((paragraph: string, index: number) => (
                <p key={index}>{paragraph}</p>
              ))}
              <p className="text-xl font-light italic pt-4">
                {content.about?.quote}
              </p>
            </div>
            
            <div>
              <img 
                src="/stefy.JPG" 
                alt="Stefy - Inner Wealth Coach" 
                className="stefy-photo rounded-lg shadow-card w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The Inner Wealth Shift */}
      <section id="shift" className="section-padding bg-navy-light">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light text-white mb-8 leading-tight">
              {content.shift?.title}
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-3xl md:text-4xl font-light text-white italic mb-8">
                {content.shift?.subtitle}
              </p>
            </div>
          </div>
          
          <div className="mb-16" ref={shiftSectionRef}>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {(content.shift?.items || []).map((item: string, index: number) => (
                <div key={index} className={`wealth-card wealth-card-${index + 1} bg-navy p-6 md:p-8 rounded-lg border-2 border-white hover-lift`}>
                  <div className="flex md:block items-start gap-4 md:gap-0">
                    <div className="text-white text-4xl md:mb-6 wealth-icon flex-shrink-0">✦</div>
                    <p className="text-blue text-lg leading-relaxed">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="moments" className="section-padding bg-white border-t border-navy/10">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h3 className="text-3xl md:text-4xl font-light text-navy mb-12 text-center">Inner Wealth Moments</h3>
          <p className="text-lg md:text-xl font-light text-navy/95 text-center max-w-3xl mx-auto -mt-6 mb-12">
            These are moments when I felt calm, alive, and truly at home within myself. When do you feel that way?
          </p>
          <ImageCarousel 
            images={[
              '/stef1.JPG',
              '/stef3.JPG',
              '/stef4.JPG',
              '/stef2.JPG'
            ]}
          />
        </div>
      </section>

      {/* The Offer */}
      <section id="offer" className="section-padding bg-navy-light">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-white mb-4">{content.offer?.title}</h2>
            <h3 className="text-3xl font-light text-white mb-8">{content.offer?.subtitle}</h3>
            <p className="text-xl text-blue leading-relaxed max-w-3xl mx-auto">
              {content.offer?.description}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-lg shadow-card">
              <h4 className="text-2xl font-light text-navy mb-6">This is not:</h4>
              <ul className="space-y-4 text-navy">
                {content.offer?.not_list?.map((item: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="text-navy mr-3 text-xl">×</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-card">
              <h4 className="text-2xl font-light text-navy mb-6">This is:</h4>
              <ul className="space-y-4 text-navy">
                {content.offer?.is_list?.map((item: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="text-navy mr-3 text-xl">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="space-y-20">
            <div className="grid md:grid-cols-3 gap-8" ref={investmentTilesRef}>
              <div className="investment-tile investment-tile-1 bg-white p-10 rounded-lg text-center shadow-card">
                <div className="text-navy text-5xl font-light mb-3">{content.offer?.investment}</div>
                <div className="text-navy text-lg">Investment</div>
              </div>
              <div className="investment-tile investment-tile-2 bg-white p-10 rounded-lg text-center shadow-card">
                <div className="text-navy text-5xl font-light mb-3">{content.offer?.duration}</div>
                <div className="text-navy text-lg">Duration</div>
              </div>
              <div className="investment-tile investment-tile-3 bg-white p-10 rounded-lg text-center shadow-card">
                <div className="text-navy text-5xl font-light mb-3">{content.offer?.format}</div>
                <div className="text-navy text-lg">Private Work</div>
              </div>
            </div>
            
            <div className="text-center px-6">
              <button 
                onClick={() => setShowApplication(true)}
                className="btn-primary"
              >
                Apply for a Complimentary 30-Minute Call
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section id="who-this-is-for" className="section-padding bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-navy mb-6">{content.who_for?.title}</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-navy p-6 md:p-8 rounded-lg shadow-card">
              <h3 className="text-2xl font-light text-white mb-6">This work is for you if:</h3>
              <ul className="space-y-4 text-lg text-blue">
                {content.who_for?.for_you?.map((item: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="text-white mr-3">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-navy p-6 md:p-8 rounded-lg shadow-card">
              <h3 className="text-2xl font-light text-white mb-6">This work is not for you if:</h3>
              <ul className="space-y-4 text-lg text-blue">
                {content.who_for?.not_for_you?.map((item: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="text-white mr-3">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="section-padding" style={{ backgroundImage: 'linear-gradient(rgba(10, 22, 40, 0.6), rgba(10, 22, 40, 0.6)), url("/Rich people.png")', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-16 text-center">Testimonials</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="testimonial-card">
              <div className="testimonial-icon-container">
                <div className="testimonial-photo-placeholder" style={{ backgroundImage: 'url("/charles.jpeg")' }}></div>
                <div className="testimonial-star-rating">
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                </div>
              </div>
              <p className="testimonial-text">
                Stefy is an amazing coach! I truly appreciate how her coaching focuses on helping you remember who you truly are, rather than trying to change you. Her approach is not only empathic and deeply connective, but she also creates a safe, non-judgmental space in every session. I value that she isn't afraid to share her own experiences, which makes each session feel genuine and supportive. Thanks to Stefy's guidance, I've become more confident in my decisions and feel much more at peace.
              </p>
              <div className="testimonial-author">
                <div className="testimonial-author-name">Sarah Mitchell</div>
                <div className="testimonial-author-details">London, UK • December 2025</div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="testimonial-card">
              <div className="testimonial-icon-container">
                <div className="testimonial-photo-placeholder" style={{ backgroundImage: 'url("/bara1.jpeg")' }}></div>
                <div className="testimonial-star-rating">
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                </div>
              </div>
              <p className="testimonial-text">
                Before my meeting with Stefy, I had no idea how much my inner saboteurs were laying obstacles in my life. I just knew I kept repeating the same patterns and mistakes. Stefy introduced me to the concept of saboteurs through a test, and it started to make sense what was happening. I've always considered myself a self-aware person, but this was a whole new level of insight. Her clear, relatable explanations helped me understand the root of these patterns. She also provided me with tools to handle them when they show up in my day-to-day life. What really made the difference was her honest, thought-provoking questions and the practical tools she shared.
              </p>
              <div className="testimonial-author">
                <div className="testimonial-author-name">Emma Thompson</div>
                <div className="testimonial-author-details">Edinburgh, UK • October 2025</div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="testimonial-card">
              <div className="testimonial-icon-container">
                <div className="testimonial-photo-placeholder" style={{ backgroundImage: 'url("/charis.jpeg")' }}></div>
                <div className="testimonial-star-rating">
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                  <span className="testimonial-star">★</span>
                </div>
              </div>
              <p className="testimonial-text">
                Stefy has helped me explore and understand my own inner saboteurs on a much deeper level. Through thoughtful and sensitive questions, she guided me on a journey of self-discovery, uncovering areas I wasn't even aware of. I'm truly grateful for our session together and highly recommend Stefy as a mental coach. Even if you think you don't need one, she will prove you wrong by unlocking insights and unconscious patterns you didn't know were there.
              </p>
              <div className="testimonial-author">
                <div className="testimonial-author-name">James Anderson</div>
                <div className="testimonial-author-details">Manchester, UK • November 2025</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section id="apply" className="bg-white" style={{ paddingTop: '2rem', paddingBottom: '4rem' }}>
        <div className="container-narrow text-center px-4 md:px-0">
          <h2 className="text-3xl font-light text-navy mb-8">{content.cta?.title}</h2>
          <p className="text-lg text-navy mb-8 leading-relaxed max-w-2xl mx-auto">
            {content.cta?.description1}
          </p>
          <p className="text-lg text-navy mb-12 leading-relaxed max-w-2xl mx-auto">
            {content.cta?.description2}
          </p>
          <button 
            onClick={() => setShowApplication(true)}
            className="btn-primary-blue"
          >
            {content.cta?.button}
          </button>
        </div>
      </section>
      
      <Footer />

      {/* Cookie Consent Popup */}
      {showCookieConsent && (
        <div className="cookie-consent-overlay">
          <div className="cookie-consent-popup">
            <p className="text-white text-sm leading-relaxed flex-1">
              We use cookies to enhance your browsing experience and analyze our traffic. 
              By clicking "Accept", you consent to our use of cookies.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => handleCookieConsent(false)}
                className="px-6 py-2 border-2 border-white text-white rounded-lg hover:bg-white hover:text-navy transition-colors text-sm"
              >
                Decline
              </button>
              <button
                onClick={() => handleCookieConsent(true)}
                className="px-6 py-2 bg-white text-navy rounded-lg hover:bg-blue transition-colors text-sm font-medium"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
